import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, Copy, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import ImageUploader from "@/components/ImageUploader";

const cred = { withCredentials: true };
export const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : fallback;
};

export const Card = ({ title, children, testId, right }) => (
  <section className="bg-white border border-gray-200" data-testid={testId}>
    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
      <h2 className="font-heading font-bold uppercase text-[#09090b]">{title}</h2>
      {right}
    </div>
    <div className="p-4">{children}</div>
  </section>
);

export const CopyLink = ({ label, url, testId }) => (
  <div className="flex items-center gap-2 text-xs" data-testid={testId}>
    <span className="font-semibold text-gray-500 w-28 shrink-0">{label}</span>
    <code className="flex-1 truncate bg-gray-50 border border-gray-200 px-2 py-1">{url}</code>
    <button type="button" className="p-1.5 hover:bg-gray-100" onClick={() => navigator.clipboard?.writeText(url).then(() => toast.success("Link copied"))} data-testid={`${testId}-copy`}>
      <Copy className="w-3.5 h-3.5" />
    </button>
    <a href={url} target="_blank" rel="noreferrer" className="px-2 py-1 bg-[#09090b] text-white font-semibold" data-testid={`${testId}-open`}>Open</a>
  </div>
);

const emptySlide = { kind: "announcement", title: "", body: "", image_url: "", active: true, order: 0 };

const SlideForm = ({ initial, onDone }) => {
  const [f, setF] = useState(initial || emptySlide);
  const save = async (e) => {
    e.preventDefault();
    try {
      if (initial?.slide_id) await axios.put(`${API}/big-screen/slides/${initial.slide_id}`, f, cred);
      else await axios.post(`${API}/big-screen/slides`, f, cred);
      toast.success("Slide saved");
      onDone(true);
    } catch (err) { toast.error(errMsg(err, "Could not save slide")); }
  };
  return (
    <form onSubmit={save} className="space-y-3 border border-gray-200 p-3 bg-gray-50" data-testid="slide-form">
      <div className="flex gap-2">
        {["announcement", "sponsor"].map((k) => (
          <button key={k} type="button" onClick={() => setF({ ...f, kind: k })} className={`px-3 py-1 text-xs font-bold uppercase border ${f.kind === k ? "bg-[#09090b] text-white" : "bg-white"}`} data-testid={`slide-kind-${k}`}>{k}</button>
        ))}
      </div>
      <Input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-none" data-testid="slide-title-input" />
      <Textarea placeholder="Announcement text" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="rounded-none" rows={2} data-testid="slide-body-input" />
      {f.kind === "sponsor" && <ImageUploader value={f.image_url} onChange={(url) => setF({ ...f, image_url: url })} category="sponsor-slide" label="Approved sponsor artwork" previewShape="wide" testId="slide-image" />}
      <div className="flex items-center gap-3">
        <Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="slide-active-switch" />
        <span className="text-sm">{f.active ? "Active" : "Hidden"}</span>
        <Input type="number" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) || 0 })} className="w-20 rounded-none ml-auto" title="Order" data-testid="slide-order-input" />
      </div>
      <div className="flex gap-2">
        <Button type="submit" size="sm" className="rounded-none bg-[#FFC72C] text-black hover:bg-[#E6B325]" data-testid="slide-save-btn">Save slide</Button>
        <Button type="button" size="sm" variant="outline" className="rounded-none" onClick={() => onDone(false)}>Cancel</Button>
      </div>
    </form>
  );
};

export const SlidesManager = ({ slides, selected, onToggle, reload }) => {
  const [editing, setEditing] = useState(null);
  const remove = async (s) => {
    if (!window.confirm(`Delete slide "${s.title || s.kind}"?`)) return;
    await axios.delete(`${API}/big-screen/slides/${s.slide_id}`, cred);
    reload();
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">Tick slides to rotate on this screen (none ticked = all active slides). Only upload approved sponsor artwork.</p>
      {slides.length === 0 && <p className="text-sm text-gray-400" data-testid="slides-empty">No slides yet.</p>}
      {slides.map((s) => (
        <div key={s.slide_id} className="flex items-center gap-3 text-sm border-b border-gray-100 pb-2" data-testid={`slide-row-${s.slide_id}`}>
          <input type="checkbox" checked={selected.includes(s.slide_id)} onChange={() => onToggle(s.slide_id)} data-testid={`slide-select-${s.slide_id}`} />
          {s.image_url ? <img src={s.image_url} alt="" className="w-12 h-8 object-contain bg-gray-100" /> : <span className="w-12 h-8 bg-gray-100 text-[10px] flex items-center justify-center uppercase">Text</span>}
          <span className="flex-1 truncate"><b className="uppercase text-[10px] mr-2 text-gray-400">{s.kind}</b>{s.title || s.body}</span>
          {!s.active && <span className="text-[10px] uppercase text-gray-400">hidden</span>}
          <button onClick={() => setEditing(s)} className="p-1 hover:bg-gray-100" data-testid={`slide-edit-${s.slide_id}`}><Pencil className="w-3.5 h-3.5" /></button>
          <button onClick={() => remove(s)} className="p-1 hover:bg-red-50 text-red-600" data-testid={`slide-delete-${s.slide_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      {editing ? (
        <SlideForm initial={editing === "new" ? null : editing} onDone={(changed) => { setEditing(null); if (changed) reload(); }} />
      ) : (
        <Button size="sm" variant="outline" className="rounded-none" onClick={() => setEditing("new")} data-testid="add-slide-btn"><Plus className="w-4 h-4 mr-1" /> Add slide</Button>
      )}
    </div>
  );
};

export const OperatorsManager = ({ operators, reload }) => {
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const create = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/big-screen/operators`, f, cred);
      toast.success("Operator account created");
      setF({ name: "", email: "", password: "" });
      reload();
    } catch (err) { toast.error(errMsg(err, "Could not create operator")); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Remove operator ${o.email}?`)) return;
    await axios.delete(`${API}/big-screen/operators/${o.user_id}`, cred);
    reload();
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Operators can control screens and slides but can never edit scores or match data.</p>
      {operators.map((o) => (
        <div key={o.user_id} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2" data-testid={`operator-row-${o.user_id}`}>
          <span><b>{o.name}</b> · {o.email}</span>
          <button onClick={() => remove(o)} className="p-1 text-red-600 hover:bg-red-50" data-testid={`operator-delete-${o.user_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="operator-form">
        <Input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-none" required data-testid="operator-name-input" />
        <Input placeholder="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-none" required data-testid="operator-email-input" />
        <Input placeholder="Password (8+ chars)" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="rounded-none" required minLength={8} data-testid="operator-password-input" />
        <Button type="submit" className="rounded-none bg-[#09090b] text-white hover:bg-black" data-testid="operator-create-btn">Add operator</Button>
      </form>
    </div>
  );
};
